import { createSlice } from '@reduxjs/toolkit';
import {
  getMonitoringTransports,
  getMonitoringGeneralInfo,
  getMonitoringLocations,
  getMonitoringTeams,
  getMonitoringTransportsByTerm,
} from '../services/monitoring.service';
import {
  MonitoringFlatTransportDTO,
  MonitoringRealTimeUpdate,
  MonitoringSlice,
} from '../types';
import { MonitoringMode, TransportFuelType } from '../../shared/types';
import { computeGPSSignalMetrics } from '../../shared/utils/computeGPSSignalMetrics';
import { computeGSMSignalMetrics } from '../../shared/utils/computeGSMSignalMetrics';

const GENERAL_INITIAL_STATE = {
  avgConsumption: null,
  avgSpeed: null,
  distancePassed: null,
  drivingTime: null,
  fuelConsumption: null,
  humidity: null,
  idlingTime: null,
  parkingTime: null,
  temperature: null,
  fuelType: TransportFuelType.GASOLINE,
  isLoading: true,
  maxSpeed: null,
};

const initialState: MonitoringSlice = {
  map: {
    teams: {},
    transports: {},
    locations: {
      locationAll: [],
      selectedLocation: null,
    },
  },
  trackInfo: {
    allSelected: true,
    teamId: null,
    transportId: null,
  },
  sidebarFilters: {
    searchResults: {
      loading: false,
      data: [],
      total: 0,
      searching: false,
    },
    teamIds: [],
  },
  general: GENERAL_INITIAL_STATE,
  mode: MonitoringMode.LIVE,
};

export const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    monitoringUpdateVehicle: (
      state,
      action: { payload: MonitoringRealTimeUpdate },
    ) => {
      const vehicle = state.map.transports[action.payload.transportId];
      const updatedVehicle: Partial<MonitoringFlatTransportDTO> = {};
      if (vehicle && action.payload.data) {
        const update = action.payload.data;
        updatedVehicle.bearing = update.bearing;
        updatedVehicle.speed = update.speed;
        updatedVehicle.createdAt = update.createdAt;
        updatedVehicle.latitude = update.position.lat;
        updatedVehicle.longitude = update.position.lng;
        if (update.satellites) {
          updatedVehicle.satellites = update.satellites;
          updatedVehicle.gpsSignalMetrics = computeGPSSignalMetrics(
            update.satellites,
          );
        }
        if (update.data.gsmSignal) {
          updatedVehicle.gsmSignal = update.data.gsmSignal;
          updatedVehicle.gsmSignalMetrics = computeGSMSignalMetrics(
            update.data.gsmSignal,
          );
        }
      }
      if (action.payload.movementStatus) {
        updatedVehicle.status = action.payload.movementStatus;
      }
      state.map.transports[action.payload.transportId] = {
        ...vehicle,
        ...updatedVehicle,
      };
    },
    monitoringTrack: (
      state,
      action: { payload: Partial<MonitoringSlice['trackInfo']> },
    ) => {
      state.trackInfo.allSelected = action.payload.allSelected || false;
      state.trackInfo.teamId = action.payload.teamId || null;
      state.trackInfo.transportId = action.payload.transportId || null;
    },
    monitoringResetTrackInfo: (state) => {
      state.trackInfo = { allSelected: false, teamId: null, transportId: null };
    },
    monitoringFollowAll: (state) => {
      state.trackInfo.allSelected = true;
      state.trackInfo.teamId = null;
      state.trackInfo.transportId = null;
    },
    monitoringSearchTransport: (state) => {
      state.sidebarFilters.searchResults.searching = true;
    },
    monitoringResetSearch: (state) => {
      state.sidebarFilters.searchResults.searching = false;
    },
    monitoringFilterByTeams: (state, action) => {
      state.sidebarFilters.teamIds = action.payload;
    },
    monitoringOpenHistoryMode: (state) => {
      state.mode = MonitoringMode.HISTORY;
    },
    monitoringTriggerLiveMode: (state) => {
      state.mode = MonitoringMode.LIVE;
    },
    monitoringSelectLocation: (state, action) => {
      state.map.locations.selectedLocation =
        state.map.locations.locationAll[action.payload];
    },
    clearMonitoringData: (state) => {
      state = initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMonitoringTeams.fulfilled, (state, action) => {
      action.payload.forEach(({ id, total, name }) => {
        state.map.teams[id] = {
          id,
          total,
          name,
          transportIds: [],
          areTransportsLoaded: total === 0,
        };
      });
    });

    builder.addCase(getMonitoringTransports.fulfilled, (state, action) => {
      const { teamId, transports } = action.payload;

      state.map.teams[teamId].areTransportsLoaded = true;

      state.map.teams[teamId].transportIds = transports.map(
        (transport) => transport.id,
      );

      transports.forEach((transport) => {
        const gsmSignal = transport.device?.data?.gsmSignal;
        const satellites = transport.device?.data?.satellites;
        const lastDeviceData = transport.device && transport.device.data;
        state.map.transports[transport.id] = {
          ...transport,
          ...(lastDeviceData || {}),
          gsmSignalMetrics: computeGSMSignalMetrics(gsmSignal),
          gpsSignalMetrics: computeGPSSignalMetrics(satellites),
        };
      });
    });

    builder.addCase(
      getMonitoringTransportsByTerm.fulfilled,
      (state, action) => {
        const { drivers, total } = action.payload;
        state.sidebarFilters.searchResults = {
          loading: false,
          data: drivers,
          total,
          searching: true,
        };
      },
    );

    builder.addCase(getMonitoringTransportsByTerm.pending, (state) => {
      state.sidebarFilters = {
        ...state.sidebarFilters,
        searchResults: {
          ...initialState.sidebarFilters.searchResults,
          loading: true,
          searching: true,
        },
      };
    });

    builder.addCase(getMonitoringLocations.fulfilled, (state, action) => {
      if (action.payload.items.length > 0) {
        const newLocationsWithScheme = action.payload.items.map((item) => {
          return { [item.id]: item };
        });
        // const uniqueLocations: Record<number, LocationLightDTO> =
        state.map.locations.locationAll = Object.assign(
          {},
          ...newLocationsWithScheme,
        );
      } else {
        state.map.locations.locationAll = action.payload.items;
      }
    });

    builder.addCase(getMonitoringGeneralInfo.pending, (state) => {
      state.general = GENERAL_INITIAL_STATE;
    });

    builder.addCase(getMonitoringGeneralInfo.fulfilled, (state, action) => {
      if (action.payload) {
        state.general = action.payload;
      }
    });
  },
});

export const {
  monitoringUpdateVehicle,
  monitoringTrack,
  monitoringFollowAll,
  monitoringResetSearch,
  monitoringSearchTransport,
  monitoringFilterByTeams,
  monitoringOpenHistoryMode,
  monitoringTriggerLiveMode,
  monitoringSelectLocation,
  clearMonitoringData,
  monitoringResetTrackInfo,
} = monitoringSlice.actions;
